/**
 * IndexedDB-backed local store for the web client.
 *
 * Mirrors `mobile/src/store/db.ts`'s shape — same `getWorks`,
 * `saveWork`, `getWorkById`, `deleteWork`, `getAllPhotos`,
 * `getWorkPhotos`, `savePhoto`, `deletePhoto` API surface — so
 * call-sites in `web/src/routes/*` stay portable with mobile
 * components if we ever cross-pollinate. The mobile module wraps
 * AsyncStorage + a single encrypted blob per entity; the web wraps
 * IndexedDB with one object store per entity (works, photos,
 * voiceNotes), keyed by `id`.
 *
 * Why IndexedDB over LocalStorage:
 *   - LocalStorage's 5–10 MB ceiling falls over with ~50 jobs and
 *     even a few thumbnails. IndexedDB tolerates 100s of MB.
 *   - LocalStorage is synchronous on the main thread; IndexedDB is
 *     async, doesn't block paints when persisting hundreds of works.
 *   - IndexedDB lets us add per-entity stores (photos by `workId`,
 *     voiceNotes by `workId`) with cheap iteration via cursors.
 *
 * Encryption story:
 *   The web stores JSON-stringified objects DIRECTLY in IndexedDB
 *   for now. The threat model: IndexedDB is sandboxed per-origin;
 *   anyone with code execution on `app.solvr.es` already has the
 *   ambient teamKey. PIN-encrypting IndexedDB content with
 *   WebCrypto is queued for Fase 2 of spec 200 (per §4.3 note
 *   "MVP teamKey plain in IndexedDB is acceptable if the PC of the
 *   user is trusted"). The functions here take the cleartext
 *   shape; the encryption layer that wraps the relay ciphertext is
 *   in `web/src/sync/relay.ts` (not this layer).
 *
 * Cache:
 *   Works + photos are read into a per-store in-memory snapshot on
 *   first access and refreshed on every write. Mobile caches the
 *   entire decrypted list because every encrypt/decrypt is
 *   non-trivial; for the web, cache motivation is different — we
 *   want to render the WorkList synchronously from React without
 *   waiting on an IndexedDB transaction. The cache is invalidated
 *   on `clearAll()` and via `invalidateCache()` exported for tests.
 *
 * Migrations:
 *   Single `versionchange` handler pinned at version 1. Adding a
 *   new store later means bumping the version and creating it in
 *   the upgrade callback — see comment block in `_open()`.
 */

import type { Client, Photo, Work } from '@cuadrilla/types';

// ── Tunables ────────────────────────────────────────────────────────

// Spec 200 Fase 8 — IndexedDB names split across two physical
// databases. Originally both `db.ts` and `keys.ts` opened the
// database `cuadrilla` with different version numbers (v1 vs v2),
// which raised `The requested version (1) is less than the existing
// version (2).` whenever keys.ts opened first and db.ts followed.
// The split keeps each module's schema independent.
//
// Schema migration policy (project-wide):
//   Every schema change bumps `DB_VERSION` and adds a branch in
//   `onupgradeneeded` that PRESERVES rows (`ALTER`, never `DROP +
//   CREATE`). When a name change is unavoidable — like the v1/v2
//   collision split that spawned this file — we ship a one-shot
//   `migration.ts` that copies the rows transparently and then
//   drops the legacy database. **Never break BC. Never ask the
//   user to clear site data.**
const DB_NAME = 'cuadrilla-data';
const DB_VERSION = 3;

const STORE_WORKS = 'works';
const STORE_PHOTOS = 'photos';
const STORE_VOICE_NOTES = 'voiceNotes';
// Spec: align /clients with mobile — explicit address book persisted in
// its own object store. Mirrors `mobile/src/store/db.ts` (Client[]).
// Bumped to v2 in DB_VERSION.
const STORE_CLIENTS = 'clients';
// Branding ("Identidad de empresa") row. Single-row store keyed by a
// constant slug so `senderInfo.ts` can do a `get('current')` /
// `put({ id: 'current', ... })` without owning a UUID. Bumped to v3.
const STORE_BRANDING = 'branding';

// The mobile `Work` legacy field; both stores migrate it on read.
type LegacyWork = Work & { assignedTechnicianId?: string };

/** Voice-note entries surface from the relay as encrypted blobs the
 *  WebCrypto layer decrypts before persistence. We mirror the shape
 *  in `mobile`'s `Work.voiceNotes[]` so the same JSON works in both
 *  places. The store keys voice notes by their own id (not by
 *  workId) so cross-work moves are O(1). */
export interface VoiceNote {
  id: string;
  workId: string;
  name?: string;
  durationMs: number;
  recordedAt: number;
  /** Object URL to a Blob held by the photo viewer / voice player.
   *  Re-derived from `localPath` on each session — never persisted. */
  localPath?: string;
  /** base64-encoded encrypted blob from the relay. The viewer
   *  decrypts on-demand. */
  encrypted?: string;
}

// ── DB connection (lazy, cached) ────────────────────────────────────

let _dbPromise: Promise<IDBDatabase> | null = null;

function _open(): Promise<IDBDatabase> {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (event) => {
      const db = req.result;
      const oldVersion = event.oldVersion ?? 0;
      // v0 → v1: create the three core stores. Future schema bumps
      // append branches here, e.g.:
      //   if (oldVersion < 2 && !db.objectStoreNames.contains('invoices')) {
      //     db.createObjectStore('invoices', { keyPath: 'id' });
      //   }
      if (oldVersion < 1) {
        if (!db.objectStoreNames.contains(STORE_WORKS)) {
          db.createObjectStore(STORE_WORKS, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(STORE_PHOTOS)) {
          const photos = db.createObjectStore(STORE_PHOTOS, { keyPath: 'id' });
          // Indexed by workId so getWorkPhotos can stream cursors
          // instead of loading all photos. Useful when a single work
          // has 30+ photos and the rest of the company has thousands.
          photos.createIndex('byWorkId', 'workId', { unique: false });
        }
        if (!db.objectStoreNames.contains(STORE_VOICE_NOTES)) {
          const voice = db.createObjectStore(STORE_VOICE_NOTES, { keyPath: 'id' });
          voice.createIndex('byWorkId', 'workId', { unique: false });
        }
      }
      // v1 → v2: address-book parity with mobile. Adds a dedicated
      // `clients` store so /clients reads from an explicit list rather
      // than inferring rows from `Work.clientName`. Existing rows in
      // `works` are preserved untouched (we only ADD a new store).
      if (oldVersion < 2) {
        if (!db.objectStoreNames.contains(STORE_CLIENTS)) {
          db.createObjectStore(STORE_CLIENTS, { keyPath: 'id' });
        }
      }
      // v2 → v3: branding ("Identidad de empresa") parity with mobile.
      // Single-row store keyed by `id` so the canonical row sits at
      // `id: 'current'`. Add-only — pre-existing stores stay intact.
      if (oldVersion < 3) {
        if (!db.objectStoreNames.contains(STORE_BRANDING)) {
          db.createObjectStore(STORE_BRANDING, { keyPath: 'id' });
        }
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
    req.onblocked = () =>
      reject(new Error('indexedDB open blocked — another tab holds an older version'));
  });
  return _dbPromise;
}

/** Open the singleton IndexedDB connection. Exposed so peer stores in
 *  the same database (e.g. `senderInfo.ts`) can share the connection
 *  without re-implementing the version-bump dance. */
export function openDb(): Promise<IDBDatabase> {
  return _open();
}

/** Force the next `_open()` to start a fresh connection. Used by
 *  tests + by the caller after a `clearAll()` on logout. */
export function resetConnection(): void {
  _dbPromise = null;
}

// ── Tx helpers ──────────────────────────────────────────────────────

function _runTx<T>(
  store: string,
  mode: IDBTransactionMode,
  body: (s: IDBObjectStore) => IDBRequest<T> | T,
): Promise<T> {
  return _open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(store, mode);
        const obj = tx.objectStore(store);
        let result: T | undefined;
        const maybeReq = body(obj);
        if (maybeReq && typeof (maybeReq as IDBRequest).onsuccess === 'object') {
          (maybeReq as IDBRequest<T>).onsuccess = () =>
            (result = (maybeReq as IDBRequest<T>).result);
        } else {
          // Synchronous return path — body produced a value directly.
          result = maybeReq as T;
        }
        tx.oncomplete = () => resolve(result as T);
        tx.onerror = () => reject(tx.error ?? new Error(`indexedDB tx error (${store})`));
        tx.onabort = () => reject(tx.error ?? new Error(`indexedDB tx aborted (${store})`));
      }),
  );
}

function _getAll<T>(store: string): Promise<T[]> {
  return _open().then(
    (db) =>
      new Promise<T[]>((resolve, reject) => {
        const tx = db.transaction(store, 'readonly');
        const obj = tx.objectStore(store);
        const req = obj.getAll();
        req.onsuccess = () => resolve(req.result as T[]);
        req.onerror = () => reject(req.error ?? new Error(`getAll(${store}) failed`));
      }),
  );
}

function _getByIndex<T>(store: string, index: string, key: IDBValidKey): Promise<T[]> {
  return _open().then(
    (db) =>
      new Promise<T[]>((resolve, reject) => {
        const tx = db.transaction(store, 'readonly');
        const obj = tx.objectStore(store);
        const idx = obj.index(index);
        const req = idx.getAll(key);
        req.onsuccess = () => resolve(req.result as T[]);
        req.onerror = () => reject(req.error ?? new Error(`getByIndex(${store}, ${index}) failed`));
      }),
  );
}

// ── In-memory cache ─────────────────────────────────────────────────
//
// React routes call `getWorks()` synchronously on mount — we store
// the snapshot here so the first call goes async (priming the cache)
// and every subsequent call resolves instantly from memory. Writes
// invalidate the cache so reads always see committed state.

let _cacheWorks: Work[] | null = null;
let _cachePhotos: Photo[] | null = null;
let _cacheClients: Client[] | null = null;

/** Test seam — drops the in-memory cache without touching IndexedDB. */
export function invalidateCache(): void {
  _cacheWorks = null;
  _cachePhotos = null;
  _cacheClients = null;
}

// ── Change subscriptions ────────────────────────────────────────────
//
// Lightweight event emitter so UI components can re-fetch when the
// store mutates (saveWork, deleteWork, etc.). Used by the live-sync
// pipeline: incoming relay messages decrypt → saveWork → emit →
// WorkList re-renders without a manual refresh. Three event types
// are enough for the Fase 4 surface; we collapse to a single
// "store mutated" event so subscribers don't have to enumerate
// each mutation kind.

type StoreListener = () => void;
const _storeListeners = new Set<StoreListener>();

/** Subscribe to "the works/photos/voice-notes store changed".
 *  Returns an unsubscribe. Use sparingly — every listener fires on
 *  every mutation, so prefer hoisting subscriptions to the top of
 *  the route over per-card subscriptions. */
export function subscribeStore(listener: StoreListener): () => void {
  _storeListeners.add(listener);
  return () => {
    _storeListeners.delete(listener);
  };
}

function _emitStoreChange(): void {
  for (const cb of _storeListeners) {
    try {
      cb();
    } catch {
      // A listener throwing must not break the emit chain — skip it
      // and keep firing the rest. The throwing component will hit
      // its own error boundary.
    }
  }
}

// ── Works ───────────────────────────────────────────────────────────

/** Migrate the legacy `assignedTechnicianId` (single id) to
 *  `assignedTechnicians` (array). Runs on every read; the write
 *  path normalises so the migration is idempotent. */
function _migrateWork(w: LegacyWork): Work {
  if (w.assignedTechnicianId && (!w.assignedTechnicians || w.assignedTechnicians.length === 0)) {
    w.assignedTechnicians = [w.assignedTechnicianId];
    delete w.assignedTechnicianId;
  }
  return w;
}

async function _readWorks(): Promise<Work[]> {
  if (_cacheWorks !== null) return _cacheWorks;
  const raw = await _getAll<LegacyWork>(STORE_WORKS);
  _cacheWorks = raw.map(_migrateWork);
  return _cacheWorks;
}

/** Returns active works only by default; pass `true` to include
 *  archived. Mirrors `mobile/src/store/db.ts#getWorks`. */
export async function getWorks(includeArchived = false): Promise<Work[]> {
  const all = await _readWorks();
  return includeArchived ? all : all.filter((t) => !t.archived);
}

export async function getWorkById(id: string): Promise<Work | null> {
  const all = await _readWorks();
  return all.find((t) => t.id === id) ?? null;
}

export async function saveWork(work: Work): Promise<void> {
  const normalised = _migrateWork(work as LegacyWork);
  await _runTx<IDBValidKey>(STORE_WORKS, 'readwrite', (s) => s.put(normalised));
  // Update cache: replace if present, prepend otherwise (matches
  // mobile's "newest-first" ordering).
  if (_cacheWorks === null) {
    _cacheWorks = [normalised];
  } else {
    const idx = _cacheWorks.findIndex((t) => t.id === normalised.id);
    if (idx >= 0) _cacheWorks[idx] = normalised;
    else _cacheWorks.unshift(normalised);
  }
  _emitStoreChange();
}

export async function deleteWork(id: string): Promise<void> {
  await _runTx<undefined>(STORE_WORKS, 'readwrite', (s) => s.delete(id));
  if (_cacheWorks !== null) {
    _cacheWorks = _cacheWorks.filter((t) => t.id !== id);
  }
  _emitStoreChange();
}

// ── Photos ──────────────────────────────────────────────────────────

async function _readPhotos(): Promise<Photo[]> {
  if (_cachePhotos !== null) return _cachePhotos;
  _cachePhotos = await _getAll<Photo>(STORE_PHOTOS);
  return _cachePhotos;
}

export async function getAllPhotos(): Promise<Photo[]> {
  return _readPhotos();
}

/** Cursor-friendly variant: queries the `byWorkId` index instead of
 *  loading every photo into memory. Useful for `WorkDetail` on a
 *  cold cache (e.g. session restore) where the photo list could be
 *  thousands of rows. */
export async function getWorkPhotos(workId: string): Promise<Photo[]> {
  if (_cachePhotos !== null) {
    return _cachePhotos.filter((p) => p.workId === workId);
  }
  return _getByIndex<Photo>(STORE_PHOTOS, 'byWorkId', workId);
}

export async function savePhoto(photo: Photo): Promise<void> {
  await _runTx<IDBValidKey>(STORE_PHOTOS, 'readwrite', (s) => s.put(photo));
  if (_cachePhotos === null) {
    _cachePhotos = [photo];
  } else {
    const idx = _cachePhotos.findIndex((p) => p.id === photo.id);
    if (idx >= 0) _cachePhotos[idx] = photo;
    else _cachePhotos.unshift(photo);
  }
  _emitStoreChange();
}

export async function deletePhoto(id: string): Promise<void> {
  await _runTx<undefined>(STORE_PHOTOS, 'readwrite', (s) => s.delete(id));
  if (_cachePhotos !== null) {
    _cachePhotos = _cachePhotos.filter((p) => p.id !== id);
  }
  _emitStoreChange();
}

export async function getPhotoCount(): Promise<Record<string, number>> {
  const all = await _readPhotos();
  const out: Record<string, number> = {};
  for (const p of all) {
    out[p.workId] = (out[p.workId] ?? 0) + 1;
  }
  return out;
}

// ── Voice notes ─────────────────────────────────────────────────────

export async function saveVoiceNote(vn: VoiceNote): Promise<void> {
  await _runTx<IDBValidKey>(STORE_VOICE_NOTES, 'readwrite', (s) => s.put(vn));
  // Notify subscribers so WorkDetail re-fetches its voice-note
  // list once the binary download patches the row's localPath.
  _emitStoreChange();
}

export async function getVoiceNote(id: string): Promise<VoiceNote | null> {
  return _runTx<VoiceNote | null>(STORE_VOICE_NOTES, 'readonly', (s) => {
    const req = s.get(id);
    // Wrap the get to keep the null fallback typed cleanly.
    return new Promise<VoiceNote | null>((resolve, reject) => {
      req.onsuccess = () => resolve((req.result as VoiceNote | undefined) ?? null);
      req.onerror = () => reject(req.error);
    }) as unknown as VoiceNote | null;
  });
}

export async function getWorkVoiceNotes(workId: string): Promise<VoiceNote[]> {
  return _getByIndex<VoiceNote>(STORE_VOICE_NOTES, 'byWorkId', workId);
}

export async function deleteVoiceNote(id: string): Promise<void> {
  await _runTx<undefined>(STORE_VOICE_NOTES, 'readwrite', (s) => s.delete(id));
  _emitStoreChange();
}

// ── Bulk operations ─────────────────────────────────────────────────

/** Wipe every store + caches. Called on logout, sesión revoked, or
 *  after a wipe message from another device. */
export async function clearAll(): Promise<void> {
  invalidateCache();
  await _runTx<undefined>(STORE_WORKS, 'readwrite', (s) => s.clear());
  await _runTx<undefined>(STORE_PHOTOS, 'readwrite', (s) => s.clear());
  await _runTx<undefined>(STORE_VOICE_NOTES, 'readwrite', (s) => s.clear());
  await _runTx<undefined>(STORE_CLIENTS, 'readwrite', (s) => s.clear());
  await _runTx<undefined>(STORE_BRANDING, 'readwrite', (s) => s.clear());
}

// ── Clients (address book) ──────────────────────────────────────────
//
// Mirrors `mobile/src/store/db.ts` Clients section. The agenda is an
// EXPLICIT, persistent list of `Client` rows — NOT a derivation over
// `Work.clientName`. This is what the user "manages" on the /clients
// page (create, edit, delete) and what the ClientPickerModal on
// mobile picks from when creating a Work.
//
// Sync: incoming `client_upsert` / `client_delete` from the relay are
// dispatched in `web/src/sync/useLiveSync.ts` and reach this store via
// `applyClientUpsert` / `applyClientDelete`. Outgoing broadcasts live
// in `web/src/sync/syncClients.ts` — call `broadcastClient` /
// `broadcastClientDelete` from UI code instead of these primitives if
// you also want peer devices to receive the change.

async function _readClients(): Promise<Client[]> {
  if (_cacheClients !== null) return _cacheClients;
  const raw = await _getAll<Client>(STORE_CLIENTS);
  _cacheClients = raw;
  return _cacheClients;
}

/** Returns every entry in the local address book.
 *  Order: insertion order from IndexedDB (`getAll`). Callers that
 *  need a specific order (alpha / recent) sort at the call-site. */
export async function getClients(): Promise<Client[]> {
  return _readClients();
}

export async function getClientById(id: string): Promise<Client | null> {
  const all = await _readClients();
  return all.find((c) => c.id === id) ?? null;
}

/** Persist a Client locally. Does NOT broadcast — for that, call
 *  `broadcastClient` from `sync/syncClients.ts`. */
export async function saveClientLocal(client: Client): Promise<void> {
  await _runTx<IDBValidKey>(STORE_CLIENTS, 'readwrite', (s) => s.put(client));
  if (_cacheClients === null) {
    _cacheClients = [client];
  } else {
    const idx = _cacheClients.findIndex((c) => c.id === client.id);
    if (idx >= 0) _cacheClients[idx] = client;
    else _cacheClients.unshift(client);
  }
  _emitStoreChange();
}

/** Delete a Client locally. Does NOT broadcast — for that, call
 *  `broadcastClientDelete` from `sync/syncClients.ts`. */
export async function deleteClientLocal(id: string): Promise<void> {
  await _runTx<undefined>(STORE_CLIENTS, 'readwrite', (s) => s.delete(id));
  if (_cacheClients !== null) {
    _cacheClients = _cacheClients.filter((c) => c.id !== id);
  }
  _emitStoreChange();
}

/** Apply an incoming `client_upsert` from the relay. Last-write-wins
 *  on `updatedAt`: a stale broadcast (older `updatedAt` than what we
 *  already hold) is ignored to avoid resurrecting a deleted-then-
 *  re-created entry from a peer with clock drift. Mirrors mobile's
 *  `processClientSync` semantics. */
export async function applyClientUpsert(client: Client): Promise<void> {
  const local = await getClientById(client.id);
  if (local && local.updatedAt >= client.updatedAt) return;
  await saveClientLocal(client);
}

/** Apply an incoming `client_delete` from the relay. */
export async function applyClientDelete(id: string): Promise<void> {
  await deleteClientLocal(id);
}
